import Point from "../xp/geom/Point";
import Rectangle from"shared/util/Rectangle";

"use strict";

interface RectangleDescriptor {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface DOMRectangle {
    x: number;
    y: number;
    width: number;
    height: number;
    left: number;
    top: number;
    right: number;
    bottom: number;
}

interface VerticalBounds {
    top: number;
    height: number;
}
/**
 * Math utility functions.
 *
 * @class MathUtil
 */
export default class MathUtil {

    /**
     * Checks for the equivalence of two floating-point numbers within a given epsilon.
     *
     * @param {Number} fp1 The first number to check.
     * @param {Number} fp2 The second number to check.
     * @param epsilon
     * @returns {boolean}
     */
    static fpEqual(fp1: number, fp2: number, epsilon: number): boolean {
        return Math.abs(fp1 - fp2) < epsilon;
    }

    /**
     * Rounds a value to the passed number of decimals places.
     *
     * @param {Number} value The value to round.
     * @param {Number} numberOfDecimals The number of decimals.
     * @returns {number} The rounded value.
     */
    static round(value: number, numberOfDecimals: number = 0): number {
        const decimals =  Math.pow(10, numberOfDecimals);
        return Math.round(value * decimals) / decimals;
    }

    /**
     * Rotates the specified point around the given center point.
     *
     * @param  {Point} point         The point to rotate.
     * @param  {Point} rotationPoint The center point origin.
     * @param  {Number} degrees      The rotation angle, in degrees.
     * @return {Point}               The rotated point.
     */
    static rotatePoint(point: Point, rotationPoint: Point, degrees: number): Point {
        const radians = (Math.PI / 180) * degrees;
        return {
            x: (rotationPoint.x + ((point.x - rotationPoint.x) * Math.cos(radians))) - ((point.y - rotationPoint.y) * Math.sin(radians)),
            y: (rotationPoint.y + ((point.x - rotationPoint.x) * Math.sin(radians))) + ((point.y - rotationPoint.y) * Math.cos(radians))
        };
    }

    /**
     * Type describing the rectangle.
     *
     * We wanna avoid the actual <tt>Rectangle</tt> class here, due to the dependencies it
     * carries (Class.extend). This module is used by "channels".
     *
     * @typedef {Object} RectangleDescriptor
     *
     * @property {Number} x - The left coordinate, x axis.
     * @property {Number} y - The top coordinate, y axis.
     * @property {Number} width - The width.
     * @property {Number} height - The height.
     */

    /**
     * Rotates all coordinates for the specified rectangle and returns a new <tt>RectangleDescriptor</tt>.
     *
     * @param  {Number} options.x      The x coordinate.
     * @param  {Number} options.y      The y coordinate.
     * @param  {Number} options.width  The width of the rectangle.
     * @param  {Number} options.height The height of the rectangle.
     * @param  {Point} rotationPoint   The rotation point.
     * @param  {Number} degrees        The rotation angle, in degrees.
     * @return {RectangleDescriptor}   The rotated <tt>RectangleDescriptor</tt>.
     */
    static rotateRect({x, y, width, height}: RectangleDescriptor, rotationPoint: Point, degrees: number): RectangleDescriptor {
        const topLeft = MathUtil.rotatePoint({x, y}, rotationPoint, degrees);
        const topRight = MathUtil.rotatePoint({x: x + width, y}, rotationPoint, degrees);
        const bottomLeft = MathUtil.rotatePoint({x, y: y + height}, rotationPoint, degrees);
        const bottomRight = MathUtil.rotatePoint({x: x + width, y: y + height}, rotationPoint, degrees);

        const xs = [topLeft.x, topRight.x, bottomRight.x, bottomLeft.x];
        const ys = [topLeft.y, topRight.y, bottomRight.y, bottomLeft.y];
        const minX = Math.min(...xs);
        const minY = Math.min(...ys);
        const maxX = Math.max(...xs);
        const maxY = Math.max(...ys);

        return {
            x: minX,
            y: minY,
            width: maxX - minX,
            height: maxY - minY
        };
    }

    /**
     * Returns `true` if the specified point is contained within the provided rectangle, `false` if not.
     *
     * @param {Rectangle|Object} rect The rectangle to test for collision.
     * @param {Object} point The collision point
     * @param {Number} point.x The x coordinate.
     * @param {Number} point.y The y coordinate.
     * @param {Number} [angle=0] The rotation angle, in degrees.
     * @param {Point} [rotationPoint=null] Optional rotation point, will default to the rectangle's x and y coordinates.
     * @return {Boolean} `true` for collision, `false` if not.
     */
    static pointInsideRect(rect: Rectangle, {x, y}: Point, angle: number = 0, rotationPoint: Point = null): boolean {
        const rotatedPoint = MathUtil.rotatePoint({x, y}, rotationPoint || {x: rect.x, y: rect.y}, angle);

        return rotatedPoint.x >= rect.x &&
                rotatedPoint.x <= (rect.x + rect.width) &&
                rotatedPoint.y >= rect.y &&
                rotatedPoint.y <= (rect.y + rect.height);
    }

    static getDistance(p1: Rectangle, p2: Rectangle): number {
        const a = p2.left - p1.left;
        const b = p2.top - p1.top;

        return Math.sqrt((a * a) + (b * b));
    }

    static getAngle(p1: Rectangle, p2: Rectangle): number {
        return Math.atan2(p2.top - p1.top, p2.left - p1.left) * (180 / Math.PI);
    }

    /**
     * Make sure value is between minimum and maximum: If value is between min and max, return
     * value. If value is smaller than min, return min. If value is bigger than max, return
     * max.
     */
    static between(value: number, min: number, max: number): number {
        return Math.max(Math.min(value, max), min);
    }

    /**
     * Calculate how much one range overlaps another range. The result is returned as a
     * fraction of the size of the overlapping range. If the overlapping range does not overlap
     * a negative value is retuned.
     *
     * @param {Number} overlappedRangeStart
     * @param {Number} overlappedRangeEnd
     * @param {Number} overlappingRangeStart
     * @param {Number} overlappingRangeEnd
     * @returns {Number} Returns a fraction between 0 and 1.
     */
    static overlapFraction(overlappedRangeStart: number, overlappedRangeEnd: number, overlappingRangeStart: number, overlappingRangeEnd: number): number {
        const overlappingRangeSize = overlappingRangeEnd - overlappingRangeStart;

        if (overlappingRangeSize === 0) {
            return 1;
        }

        const overlapStart = Math.max(overlappedRangeStart, overlappingRangeStart);
        const overlapEnd = Math.min(overlappedRangeEnd, overlappingRangeEnd);
        const overlapSize = overlapEnd - overlapStart;
        const overlappingFragment = overlapSize / overlappingRangeSize;

        return overlappingFragment;
    }

    /**
     * Type describing the vertical bounds.
     *
     * @typedef {Object} VerticalBounds
     *
     * @property {Number} top - The top distance.
     * @property {Number} height - The height.
     */

    /**
     * Given a viewport's bounds, computes the joined bounds from the specified target bounds.
     *
     * The methods only focuses on the vertical axis.
     *
     * @param  {Object|Rectangle} viewportBounds The viewport bounds.
     * @param  {Object|Rectangle} targetBounds   The target bounds.
     * @return {VerticalBounds}                  The vertical bounds.
     */
    static computeVerticalBounds(targetBounds: Rectangle, viewportBounds: Rectangle): VerticalBounds {
        const viewportHeight = viewportBounds.height;
        const viewportBottom = viewportBounds.top + viewportHeight;
        const containerHeight = targetBounds.height;
        const containerTop = targetBounds.top + viewportBounds.top;
        const containerBottom = containerTop + containerHeight;
        const verticalOverlapFraction = MathUtil.overlapFraction(containerTop, containerBottom, viewportBounds.top, viewportBottom);

        return {
            top: MathUtil.between(viewportBounds.top - containerTop, 0, containerHeight),
            height: MathUtil.between(verticalOverlapFraction * viewportHeight, 0, viewportHeight)
        };
    }
    /**
    * The idea of method is to round the input long float percentage value.
    * The rounding is done to exactly contain all integer limiter possible values 
    * Returns rounded percentage value
    * @param  {Number} value      The percent(!) value to round.
    * @param  {Number} limiter    The limiter value.
    * @return {Number}            The rounded value.
    */
    static roundToContain(value: number, limiter: number): number {
        const bitDepth = limiter.toString().length;
        const realSizeInPixels = (value * limiter) / 100;
        const integerPixelsValue = Math.round(realSizeInPixels);
        const correctToContain = integerPixelsValue / (limiter / 100);
        return MathUtil.round(correctToContain, bitDepth - 2);
    }

    /**
     * Type describing the rectangle.
     *
     * @typedef {Object} DOMRectangle
     *
     * @property {Number} x - The left coordinate, x axis.
     * @property {Number} y - The top coordinate, y axis.
     * @property {Number} width - The width.
     * @property {Number} height - The height.
     * @property {Number} left - The left.
     * @property {Number} top - The top.
     * @property {Number} right - The right.
     * @property {Number} bottom - The bottom.
     */

     /**
     * Rouns all <tt>DOMRectangle</tt> property, according to the experience size to contain it
     * and returns a new <tt>DOMRectangle</tt>.
     * @param  {DOMRectangle} rect      The x coordinate.
     * @param  {Number} experienceWidth      The experience widht.
     * @param  {Number} experienceHeight      The experience height.
     * @return {DOMRectangle}   The rotated <tt>DOMRectangle</tt>.
     */
    static roundRect(rect: DOMRectangle): DOMRectangle {
        return {
            x: Math.round(rect.x),
            y: Math.round(rect.y),
            width: Math.round(rect.width),
            height: Math.round(rect.height),
            left: Math.round(rect.left),
            top: Math.round(rect.top),
            right: Math.round(rect.right),
            bottom: Math.round(rect.bottom)
        };
    }

    /**
     * Scales a dimension by the give experience size (ie either height or width)
     *
     */
    static scaleByExperienceSize(dimension, experienceSize) {
        return dimension / experienceSize * 100;
    }

    /**
     * Reverse scale a dimension by the give experience size (ie either height or width)
     *
     */
    static invertScaleByExperienceSize(dimension, experienceSize) {
        return dimension * experienceSize / 100;
    }

};



// WEBPACK FOOTER //
// ./src/shared/util/MathUtil.ts